import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import Image from "next/image";
import {
  SubscriptionCustomer,
  SubscriptionCollectionMethod,
  Product,
} from "@/types/subscription";
import { LoaderCircle, Minus, Plus, X } from "lucide-react";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Libraries, useLoadScript } from "@react-google-maps/api";
import { validateEmail } from "@/utils/utils";
import {
  validatePromotionCode,
  ValidatedPromotionCode,
} from "@/services/subscription.client";

interface SubscriptionPaymentMethodDialogProps {
  controlledOpen?: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (
    paymentMethod: SubscriptionCollectionMethod,
    customer?: SubscriptionCustomer | string,
    quantity?: number,
    promotionCodeId?: string,
  ) => void | Promise<void>;
  customerId: string;
  product: Product;
  subscriptionMetadata: any;
}

const libraries: Libraries = ["places"];

export default function SubscriptionPaymentMethodDialog({
  controlledOpen,
  onOpenChange,
  onSuccess,
  customerId,
  product,
  subscriptionMetadata,
}: SubscriptionPaymentMethodDialogProps) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries,
  });

  const SELECT_PAYMENT_METHOD_TITLE = "What's your preferred payment method?";
  const SET_SUBSCRIPTION_SEATS_TITLE = `How many ${product === "poweron-pro" ? "seats" : "hosts"} do you need?`;
  const SET_COUPON_CODE_TITLE = "Do you have a coupon code?";
  const SET_BILLING_DETAILS_TITLE =
    "What are the billing details for the Invoice?";

  const [paymentMethod, setPaymentMethod] =
    useState<SubscriptionCollectionMethod>(
      SubscriptionCollectionMethod.AUTOMATIC,
    );
  const [title, setTitle] = useState(SELECT_PAYMENT_METHOD_TITLE);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubscriptionSeats, setHasSubscriptionSeats] = useState(false);
  const [hasCouponStep, setHasCouponStep] = useState(false);
  const [isInvoiceSelected, setIsInvoiceSelected] = useState(false);
  const [customer, setCustomer] = useState<SubscriptionCustomer | null>(null);
  const [quantity, setQuantity] = useState<number | null>(1);
  const [isCustomerValid, setIsCustomerValid] = useState(false);

  // Coupon code state
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [appliedPromotion, setAppliedPromotion] =
    useState<ValidatedPromotionCode | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const couponDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced coupon validation
  useEffect(() => {
    // Clear any existing timeout
    if (couponDebounceRef.current) {
      clearTimeout(couponDebounceRef.current);
    }

    // Don't validate if empty or already have a valid promotion
    if (!couponCode.trim() || appliedPromotion) {
      return;
    }

    // Set up debounced validation (1 second)
    couponDebounceRef.current = setTimeout(async () => {
      setIsValidatingCoupon(true);
      setCouponError(null);

      try {
        const promoCode = await validatePromotionCode(couponCode.trim());
        setAppliedPromotion(promoCode);
      } catch (error: any) {
        setCouponError(error.message || "Invalid promotion code");
        setAppliedPromotion(null);
      } finally {
        setIsValidatingCoupon(false);
      }
    }, 1000);

    return () => {
      if (couponDebounceRef.current) {
        clearTimeout(couponDebounceRef.current);
      }
    };
  }, [couponCode, appliedPromotion]);

  const handleRemoveCoupon = () => {
    setAppliedPromotion(null);
    setCouponError(null);
    setCouponCode("");
  };

  const calculateDiscountedPrice = useCallback(
    (originalPrice: number): number => {
      if (!appliedPromotion) return originalPrice;

      const { coupon } = appliedPromotion;
      if (coupon.percentOff) {
        return originalPrice * (1 - coupon.percentOff / 100);
      }
      if (coupon.amountOff) {
        // amountOff is in cents
        return Math.max(0, originalPrice - coupon.amountOff / 100);
      }
      return originalPrice;
    },
    [appliedPromotion],
  );

  const getDiscountDescription = useCallback((): string => {
    if (!appliedPromotion) return "";

    const { coupon } = appliedPromotion;
    if (coupon.percentOff) {
      let desc = `${coupon.percentOff}% off`;
      if (coupon.duration === "once") {
        desc += " (first payment)";
      } else if (coupon.duration === "repeating" && coupon.durationInMonths) {
        desc += ` (${coupon.durationInMonths} months)`;
      } else if (coupon.duration === "forever") {
        desc += " (forever)";
      }
      return desc;
    }
    if (coupon.amountOff && coupon.currency) {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: coupon.currency.toUpperCase(),
      }).format(coupon.amountOff / 100);
      let desc = `${formatted} off`;
      if (coupon.duration === "once") {
        desc += " (first payment)";
      } else if (coupon.duration === "repeating" && coupon.durationInMonths) {
        desc += ` (${coupon.durationInMonths} months)`;
      } else if (coupon.duration === "forever") {
        desc += " (forever)";
      }
      return desc;
    }
    return "";
  }, [appliedPromotion]);

  const formatPrice = useCallback(
    (amount: number, includeCents = false): string => {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
      return includeCents ? formatted : formatted.slice(0, -3);
    },
    [],
  );

  const getOriginalPrice = useCallback((): number => {
    return (
      (product === "poweron-pro"
        ? subscriptionMetadata.powerOnProPrice
        : subscriptionMetadata.powerOnPipelinesPrice) * (quantity || 1)
    );
  }, [product, subscriptionMetadata, quantity]);

  const submitSelection = async (
    ...args: Parameters<SubscriptionPaymentMethodDialogProps["onSuccess"]>
  ) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await onSuccess(...args);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCouponStepContinue = async () => {
    if (isLoading || isValidatingCoupon) return;
    if (customerId === "UNASSIGNED") {
      setTitle(SET_BILLING_DETAILS_TITLE);
      setHasSubscriptionSeats(true);
    } else {
      await submitSelection(
        paymentMethod,
        customerId,
        quantity!,
        appliedPromotion?.id,
      );
    }
  };

  const handleSelection = async () => {
    if (isLoading || isValidatingCoupon) return;
    const promoCodeId = appliedPromotion?.id;

    if (paymentMethod === SubscriptionCollectionMethod.AUTOMATIC) {
      await submitSelection(paymentMethod, undefined, undefined, promoCodeId);
      return;
    }

    if (paymentMethod === SubscriptionCollectionMethod.INVOICE) {
      // Capture billing details
      if (!isInvoiceSelected) {
        // PowerOn Pipelines doesn't need quantity selection (always 1)
        if (product === "poweron-pipelines") {
          setQuantity(1);
          // Go to coupon step
          setTitle(SET_COUPON_CODE_TITLE);
          setHasCouponStep(true);
        } else {
          setTitle(SET_SUBSCRIPTION_SEATS_TITLE);
        }
        setIsInvoiceSelected(true);
      } else if (isInvoiceSelected && !hasCouponStep) {
        // After quantity selection, go to coupon step
        setTitle(SET_COUPON_CODE_TITLE);
        setHasCouponStep(true);
      } else if (hasCouponStep && !hasSubscriptionSeats) {
        // After coupon step
        if (customerId === "UNASSIGNED") {
          setTitle(SET_BILLING_DETAILS_TITLE);
          setHasSubscriptionSeats(true);
        } else {
          await submitSelection(
            paymentMethod,
            customerId,
            quantity!,
            promoCodeId,
          );
        }
      } else {
        await submitSelection(paymentMethod, customer!, quantity!, promoCodeId);
      }
      return;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomer(
      (prev) =>
        ({
          ...prev,
          [e.target.id]: e.target.value,
        }) as SubscriptionCustomer,
    );
  };

  const handlePlaceChange = useCallback(
    async (address: any) => {
      if (!isLoaded) return;

      const place = address.getPlace();

      if (!place) {
        setCustomer(
          (prev) =>
            ({
              ...prev,
              streetAddress: "",
              streetAddress2: "",
              city: "",
              state: "",
              zipCode: "",
            }) as SubscriptionCustomer,
        );
        return;
      }

      formData(place);
    },
    [isLoaded],
  );

  const formData = (data: any) => {
    if (!data || !data.address_components) return;

    const addressComponents = data.address_components;
    const componentMap: any = {
      subPremise: "",
      premise: "",
      street_number: "",
      route: "",
      country: "",
      postal_code: "",
      locality: "",
      administrative_area_level_1: "",
    };

    for (const component of addressComponents) {
      const componentType = component.types[0];
      if (componentMap.hasOwnProperty(componentType)) {
        componentMap[componentType] = component.long_name;
      }
    }

    const formattedAddress =
      `${componentMap.subPremise} ${componentMap.premise} ${componentMap.street_number} ${componentMap.route}`.trim();

    setCustomer(
      (prev) =>
        ({
          ...prev,
          streetAddress: formattedAddress,
          city: componentMap.locality,
          state: componentMap.administrative_area_level_1,
          postalCode: componentMap.postal_code,
        }) as SubscriptionCustomer,
    );
  };

  useEffect(() => {
    // Reset the state when the dialog is closed
    if (!controlledOpen) {
      setTimeout(() => {
        setTitle(SELECT_PAYMENT_METHOD_TITLE);
        setCustomer(null);
        setIsLoading(false);
        setIsInvoiceSelected(false);
        setHasSubscriptionSeats(false);
        setHasCouponStep(false);
        setPaymentMethod(SubscriptionCollectionMethod.AUTOMATIC);
        setQuantity(1);
        // Reset coupon state
        setCouponCode("");
        setCouponError(null);
        setIsValidatingCoupon(false);
        setAppliedPromotion(null);
        setShouldShake(false);
      }, 300);
    }
  }, [controlledOpen]);

  useEffect(() => {
    if (!isLoaded || loadError || !hasSubscriptionSeats) return;

    const options = {
      componentRestrictions: { country: ["us"] },
      fields: ["address_components"],
    };

    const autoComplete = new google.maps.places.Autocomplete(
      inputRef.current!,
      options,
    );

    autoComplete.addListener("place_changed", () =>
      handlePlaceChange(autoComplete),
    );
  }, [isLoaded, loadError, handlePlaceChange, hasSubscriptionSeats]);

  useEffect(() => {
    if (!customer) {
      setIsCustomerValid(false);
      return;
    }

    const requiredFields = [
      customer.companyName,
      customer.email,
      customer.streetAddress,
      customer.city,
      customer.state,
      customer.postalCode,
    ];

    const allRequiredFilled = requiredFields.every(
      (field) => field && field.trim() !== "",
    );

    setIsCustomerValid(allRequiredFilled && !validateEmail(customer.email));
  }, [customer]);

  // State for shake animation
  const [shouldShake, setShouldShake] = useState(false);

  // Trigger shake on coupon error
  useEffect(() => {
    if (couponError && !isValidatingCoupon) {
      setShouldShake(true);
      const timer = setTimeout(() => setShouldShake(false), 300);
      return () => clearTimeout(timer);
    }
  }, [couponError, isValidatingCoupon]);

  // Render coupon code step
  const renderCouponStep = () => {
    const showHintArea = isValidatingCoupon || appliedPromotion;

    const formatSavings = (amount: number): string => {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
      // Remove trailing .00
      return formatted.replace(/\.00$/, "");
    };

    const termLabel = subscriptionMetadata.term.includes("-")
      ? subscriptionMetadata.term.replace("-", " ")
      : subscriptionMetadata.term;

    return (
      <div className="mt-2 flex w-full flex-col items-center">
        {/* Coupon input with X button inside */}
        <div className="relative w-full max-w-[200px]">
          <Label htmlFor="couponCode" className="sr-only">
            Coupon code
          </Label>
          <Input
            id="couponCode"
            value={appliedPromotion ? appliedPromotion.code : couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value.toUpperCase());
              setCouponError(null);
              setAppliedPromotion(null);
            }}
            type="text"
            autoComplete="off"
            readOnly={!!appliedPromotion}
            className={`px-8 text-center font-display tracking-wider transition-colors duration-200 focus:outline-none ${
              couponError && !isValidatingCoupon
                ? "!border-destructive"
                : appliedPromotion
                  ? "!border-emerald-500"
                  : ""
            } ${shouldShake ? "animate-shake" : ""}`}
          />
          {appliedPromotion && (
            <button
              type="button"
              onClick={handleRemoveCoupon}
              className="absolute right-2 top-1/2 -translate-y-[25%] text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        {/* Collapsible hint area with smooth height animation */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showHintArea ? "mt-3 max-h-[60px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-col items-center">
            {/* Validating state */}
            <div
              className={`flex items-center gap-2 text-muted transition-all duration-300 ${
                isValidatingCoupon
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none absolute -translate-y-2 opacity-0"
              }`}
            >
              <LoaderCircle className="size-3 animate-spin" />
              <span className="text-xs">Validating...</span>
            </div>

            {/* Valid coupon state */}
            <div
              className={`flex flex-col items-center transition-all duration-300 ${
                appliedPromotion && !isValidatingCoupon
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none absolute translate-y-2 opacity-0"
              }`}
            >
              <span className="text-xs text-muted">
                {getDiscountDescription()} &mdash;{" "}
                <span className="text-emerald-400">
                  You save{" "}
                  {formatSavings(
                    getOriginalPrice() -
                      calculateDiscountedPrice(getOriginalPrice()),
                  )}
                </span>
              </span>
              <span className="inline-flex items-baseline whitespace-nowrap text-muted">
                <span className="text-sm line-through">
                  {formatPrice(getOriginalPrice())}
                </span>
                <span className="mx-1 text-lg font-semibold text-emerald-400">
                  {formatPrice(calculateDiscountedPrice(getOriginalPrice()))}
                </span>
                <span className="text-xs">/ {termLabel}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={controlledOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card"
        onInteractOutside={(e: any) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="flex">
          <DialogTitle className="select-none pb-2 text-center text-gray-1100 dark:text-gray-100">
            {title}
          </DialogTitle>
          <DialogDescription className="!mt-0" />
          <div className="!mt-0 flex justify-center py-2">
            {isInvoiceSelected && hasSubscriptionSeats ? (
              <div className="flex w-full flex-col">
                <form>
                  <div className="grid grid-cols-4 gap-6 py-4">
                    <div className="col-span-4 sm:col-span-2">
                      <Label
                        htmlFor="companyName"
                        className="block select-none"
                      >
                        Company name
                      </Label>
                      <Input
                        id="companyName"
                        placeholder="Libum Credit Union"
                        value={customer?.companyName || ""}
                        onChange={handleChange}
                        type="text"
                        autoComplete="off"
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Label htmlFor="email" className="block select-none">
                        Billing email
                      </Label>
                      <Input
                        id="email"
                        placeholder="accounting@libum.io"
                        value={customer?.email || ""}
                        onChange={handleChange}
                        type="text"
                        autoComplete="off"
                      />
                    </div>

                    <div className="col-span-4">
                      <Label htmlFor="streetAddress" className="block">
                        Primary business address
                      </Label>
                      <Input
                        id="streetAddress"
                        ref={inputRef}
                        placeholder="Address"
                        value={customer?.streetAddress || ""}
                        onChange={handleChange}
                        type="text"
                        autoComplete="off"
                      />
                      <Input
                        id="streetAddress2"
                        placeholder="Address Line 2"
                        value={customer?.streetAddress2 || ""}
                        onChange={handleChange}
                        type="text"
                        autoComplete="off"
                      />
                      <div className="flex flex-col sm:flex-row sm:gap-2">
                        <Input
                          id="city"
                          placeholder="City"
                          value={customer?.city || ""}
                          onChange={handleChange}
                          type="text"
                          autoComplete="off"
                          className="basis-5/12"
                        />
                        <Input
                          id="state"
                          placeholder="State"
                          value={customer?.state || ""}
                          onChange={handleChange}
                          type="text"
                          autoComplete="off"
                          className="basis-4/12"
                        />
                        <Input
                          id="postalCode"
                          placeholder="Postal Code"
                          value={customer?.postalCode || ""}
                          onChange={handleChange}
                          type="text"
                          autoComplete="off"
                          className="basis-3/12"
                        />
                      </div>
                    </div>
                    <div className="col-span-4">
                      <div className="flex w-full justify-end gap-2">
                        <Image
                          src="/emojis/flag-us.svg"
                          alt=""
                          width={28}
                          height={28}
                        />
                        <span className="mt-1 select-none text-sm font-semibold">
                          United States
                        </span>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            ) : isInvoiceSelected && hasCouponStep ? (
              renderCouponStep()
            ) : isInvoiceSelected && product === "poweron-pro" ? (
              <div className="mt-2 flex w-full flex-col items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="form"
                    className="size-7 rounded-full"
                    onClick={() => setQuantity((prev) => (prev || 1) - 1 || 1)}
                  >
                    <Minus />
                  </Button>
                  <Input
                    id="quantity"
                    value={quantity || ""}
                    onChange={(e) => {
                      const raw = e.target.value;

                      if (raw === "") {
                        setQuantity(null); // allow empty
                        return;
                      }

                      let value = parseInt(raw, 10);

                      if (isNaN(value)) {
                        setQuantity(null);
                      } else {
                        if (value < 1) value = 1;
                        if (value > 999) value = 999;
                        setQuantity(value);
                      }
                    }}
                    type="number"
                    autoComplete="off"
                    className="mt-0 w-16 pb-2 text-center font-semibold"
                  />
                  <Button
                    variant="form"
                    className="size-7 rounded-full"
                    onClick={() => setQuantity((prev) => (prev || 1) + 1)}
                  >
                    <Plus />
                  </Button>
                </div>

                {/* Price display */}
                <div className="inline-flex items-baseline text-muted">
                  <span className="text-2xl font-semibold text-emerald-400">
                    {formatPrice(getOriginalPrice())}
                  </span>
                  <span className="ml-1 text-xs">
                    /{" "}
                    {subscriptionMetadata.term.includes("-")
                      ? subscriptionMetadata.term.replace("-", " ")
                      : subscriptionMetadata.term}
                  </span>
                </div>
              </div>
            ) : (
              <Tabs
                value={paymentMethod}
                onValueChange={(value) => {
                  setPaymentMethod(value as SubscriptionCollectionMethod);
                }}
                className="w-full"
              >
                <TabsList className="flex h-full select-none flex-col bg-cardTabs sm:flex-row">
                  <TabsTrigger
                    value={SubscriptionCollectionMethod.AUTOMATIC}
                    className="flex h-full flex-col py-3 transition-colors hover:!bg-card hover:text-primary-foreground data-[state=active]:!bg-card"
                  >
                    <span className="font-semibold">
                      Card or US Bank Account
                    </span>
                    <div className="flex items-center py-3">
                      <Image
                        src="/emojis/credit-card.svg"
                        alt=""
                        width={36}
                        height={36}
                      />
                      <span className="px-4 text-muted">or</span>
                      <Image
                        className="mb-1"
                        src="/emojis/bank.svg"
                        alt=""
                        width={28}
                        height={28}
                      />
                    </div>
                    <span className="text-xs text-muted">
                      You will be charged automatically
                    </span>
                  </TabsTrigger>
                  <div
                    aria-hidden="true"
                    className="h-[2px] w-4/5 shrink-0 bg-card sm:h-24 sm:w-[2px]"
                  />
                  <TabsTrigger
                    value={SubscriptionCollectionMethod.INVOICE}
                    className="relative flex h-full flex-col py-3 transition-colors hover:!bg-card hover:text-primary-foreground data-[state=active]:!bg-card"
                  >
                    <span className="font-semibold">Invoice</span>
                    <div className="flex py-3">
                      <Image
                        src="/emojis/receipt.svg"
                        alt=""
                        width={36}
                        height={36}
                      />
                    </div>
                    <span className="text-xs text-muted">
                      You will have Net 30 days to pay
                    </span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
        </DialogHeader>
        <fieldset
          disabled={isLoading}
          aria-busy={isLoading}
          className="flex min-w-0 justify-between"
        >
          {isInvoiceSelected && hasSubscriptionSeats ? (
            <>
              <div className="flex">
                <Button
                  variant="form"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <SaveButtonSpinner
                  show={isLoading || isValidatingCoupon}
                  margin="right"
                  collapseGap
                />
                <Button
                  variant="form"
                  onClick={() => {
                    setHasSubscriptionSeats(false);
                    setTitle(SET_COUPON_CODE_TITLE);
                  }}
                >
                  Back
                </Button>
                <Button onClick={handleSelection} disabled={!isCustomerValid}>
                  Subscribe
                </Button>
              </div>
            </>
          ) : isInvoiceSelected && hasCouponStep ? (
            <>
              <div className="flex">
                <Button
                  variant="form"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <SaveButtonSpinner
                  show={isLoading || isValidatingCoupon}
                  margin="right"
                  collapseGap
                />
                <Button
                  variant="form"
                  onClick={() => {
                    setHasCouponStep(false);
                    if (product === "poweron-pipelines") {
                      setIsInvoiceSelected(false);
                      setTitle(SELECT_PAYMENT_METHOD_TITLE);
                    } else {
                      setTitle(SET_SUBSCRIPTION_SEATS_TITLE);
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleCouponStepContinue}
                  disabled={isValidatingCoupon}
                >
                  {appliedPromotion
                    ? customerId === "UNASSIGNED"
                      ? "Continue"
                      : "Subscribe"
                    : "Skip"}
                </Button>
              </div>
            </>
          ) : isInvoiceSelected ? (
            <>
              <div className="flex">
                <Button
                  variant="form"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <SaveButtonSpinner
                  show={isLoading || isValidatingCoupon}
                  margin="right"
                  collapseGap
                />
                <Button
                  variant="form"
                  onClick={() => {
                    setIsInvoiceSelected(false);
                    setTitle(SELECT_PAYMENT_METHOD_TITLE);
                  }}
                >
                  Back
                </Button>
                <Button onClick={handleSelection}>Continue</Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex" />
              <div className="flex items-center gap-2">
                <SaveButtonSpinner
                  show={isLoading || isValidatingCoupon}
                  margin="right"
                  collapseGap
                />
                <Button
                  variant="form"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSelection}>
                  {paymentMethod === SubscriptionCollectionMethod.AUTOMATIC
                    ? "Subscribe"
                    : "Continue"}
                </Button>
              </div>
            </>
          )}
        </fieldset>
      </DialogContent>
    </Dialog>
  );
}
